import FormBase from 'components/form-base/FormBase';
import { Error, FieldRow, FieldRowDouble } from 'components/form-base/styled';
import { useDirections, useInternshipSettings } from 'contexts';
import DOMPurify from 'dompurify';
import { PhoneNumber, Select, TextField } from 'kl-b2c-ui-kit';
import React, { FC, useEffect } from 'react';
import { Controller, FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Register } from 'types';
import { EnglishLevel } from 'types/enums/english-level';
import { HoursPerWeek } from 'types/enums/hours-per-week';
import { PlaceholderField } from './styled';
import { CityType } from 'types/enums/city-type';

interface RegistrationFormProps {
    submit: (data: FieldValues) => void;
}

const RegistrationForm: FC<RegistrationFormProps> = ({ submit }) => {
    const { t } = useTranslation('pages/home');
    const { internshipSettings, addInternshipResponseLoading } = useInternshipSettings();
    const { directions, studyTypes, howDidYouKnowTypes } = useDirections();

    const {
        control,
        formState: { errors },
        reset,
        watch,
        setValue,
    } = useFormContext<Register>();

    const isServiceDesk = () => window.location.pathname.includes('service-desk');

    useEffect(
        () => () => {
            reset();
        },
        []
    );

    if (!directions) {
        return null;
    }

    const showCity = internshipSettings?.cityIsRequired && watch('cityId')?.valueOf() === CityType.Other;
    const cityValue = watch('cityId')?.valueOf();

    useEffect(() => {
        if (!cityValue) return;
        if (cityValue === CityType.MoscowAndArea) setValue('city', 'Москва и область');
        if (cityValue === CityType.SPB) setValue('city', 'Санкт-Петербург');
        else setValue('city', '');
    }, [cityValue]);

    return (
        <FormBase
            gap={'24px'}
            policies={[
                {
                    name: 'agreement.privacyPolicyIsSelected',
                    label: t('pp', {
                        confirmationLink: `${window.location.origin}/privacy-policy#personalDataInternshipProcessing`,
                        ppLink: '/privacy-policy',
                    }),
                    required: true,
                },
                {
                    name: 'agreement.touCheckBoxIsSelected',
                    label: t('tou', {
                        touLink: `${window.location.origin}/terms-of-use`,
                    }),
                    required: true,
                },
                {
                    name: 'agreement.marketingIsSelected',
                    label: t('marketing', {
                        confirmationLink: `${window.location.origin}/privacy-policy#personalDataInternshipProcessing`,
                        ppLink: '/privacy-policy',
                    }),
                },
            ]}
            submit={submit}
            loading={addInternshipResponseLoading}
            submitLabel={t('register')}
        >
            {internshipSettings?.interestDirectionIsRequired && (
                <Controller
                    name={'directionsId'}
                    control={control}
                    rules={{ required: t('required-field') }}
                    defaultValue={isServiceDesk() ? [directions[0].id] : undefined}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <Select
                                atSelector={{
                                    container: 'directionsIdContainer',
                                    optionsContainer: 'directionsIdOptionsContainer',
                                    optionItem: 'directionsIdOptionsItem',
                                }}
                                isMulti
                                maxLength={3}
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                disabled={isServiceDesk()}
                                defaultValues={
                                    isServiceDesk() ? [{ key: directions[0].name, value: directions[0].id }] : undefined
                                }
                                onChoose={(chosen) => {
                                    onChange(chosen?.map((c) => c.value));
                                }}
                                items={
                                    directions?.map((direction) => ({ key: direction.name, value: direction.id })) || []
                                }
                                placeholder={t('directionsId')}
                                error={!!errors['directionsId']}
                            />
                            {errors?.directionsId && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.directionsId?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.firstNameIsRequired && (
                <Controller
                    name={'firstName'}
                    control={control}
                    rules={{
                        required: t('required-field'),
                        maxLength: { value: 50, message: t('max-length', { value: 50 }) },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="firstNameSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('first-name')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['firstName']}
                            />
                            {errors?.firstName && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.firstName?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.lastNameIsRequired && (
                <Controller
                    name={'lastName'}
                    control={control}
                    rules={{
                        required: t('required-field'),
                        maxLength: { value: 50, message: t('max-length', { value: 50 }) },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="lastNameSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('last-name')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['lastName']}
                            />
                            {errors?.lastName && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.lastName?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.emailIsRequired && (
                <Controller
                    name={'email'}
                    control={control}
                    rules={{
                        required: t('required-field'),
                        maxLength: { value: 255, message: t('max-length', { value: 255 }) },
                        pattern: {
                            value: /\S+@\S+\.\S+/,
                            message: t('email-format'),
                        },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="emailSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('email')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['email']}
                            />
                            {errors?.email && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.email?.message ?? DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.phoneIsRequired && (
                <Controller
                    name={'phone'}
                    control={control}
                    rules={{
                        required: t('required-field'),
                        // @ts-ignore
                        validate: (phone: string) => {
                            // 12 is the length of output data with additional plus as first letter
                            if (phone.replace(/\s/g, '').length < 12) {
                                return t('min-length', { value: 11 });
                            }

                            if (phone.replace(/\s/g, '').length > 12) {
                                return t('max-length', { value: 11 });
                            }

                            return true;
                        },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <PhoneNumber
                                atSelector="phoneSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('phone')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['phone']}
                            />
                            {errors?.phone && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.phone?.message ?? DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.telegramNickNameIsRequired && (
                <Controller
                    name={'telegramNickName'}
                    control={control}
                    rules={{
                        required: t('required-field'),
                        maxLength: { value: 255, message: t('max-length', { value: 255 }) },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="telegramNickNameSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('telegramNickName')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['telegramNickName']}
                            />
                            {errors?.telegramNickName && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.telegramNickName?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.cityIsRequired && (
                <>
                    <Controller
                        name={'cityId'}
                        control={control}
                        rules={{ required: t('required-field') }}
                        render={({ field: { onChange, value } }) => (
                            <FieldRow>
                                <Select
                                    atSelector={{
                                        container: 'cityIdContainer',
                                        optionsContainer: 'cityIdOptionsContainer',
                                        optionItem: 'cityIdOptionsItem',
                                    }}
                                    items={Object.keys(CityType)
                                        .filter((key) => !isNaN(Number(CityType[key as keyof typeof CityType])))
                                        .map((city) => ({
                                            key: t(`city_${city}`),
                                            value: CityType[city as keyof typeof CityType],
                                        }))}
                                    variant={'border'}
                                    fullWidth
                                    value={value}
                                    placeholder={t('cityId')}
                                    onChoose={(chosen) => {
                                        if (chosen) {
                                            onChange(chosen[0].value);
                                        }
                                    }}
                                    error={!!errors['cityId']}
                                />
                                {errors?.cityId && (
                                    <Error>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    errors?.cityId?.message ??
                                                    DOMPurify.sanitize(t('validations:default')),
                                            }}
                                        />
                                    </Error>
                                )}
                            </FieldRow>
                        )}
                    />
                    {showCity && (
                        <Controller
                            name={'city'}
                            control={control}
                            rules={{
                                required: t('required-field'),
                                maxLength: { value: 50, message: t('max-length', { value: 50 }) },
                            }}
                            render={({ field: { onChange, value } }) => (
                                <FieldRow>
                                    <TextField
                                        atSelector="citySelector"
                                        fullWidth
                                        value={value}
                                        type={'text'}
                                        variant={'border'}
                                        transparent
                                        placeholder={t('city')}
                                        onChange={(value) => onChange(value)}
                                        error={!!errors['city']}
                                    />
                                    {errors?.city && (
                                        <Error>
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        errors?.city?.message ??
                                                        DOMPurify.sanitize(t('validations:default')),
                                                }}
                                            />
                                        </Error>
                                    )}
                                </FieldRow>
                            )}
                        />
                    )}
                </>
            )}
            {internshipSettings?.studyTypeIsRequired && (
                <Controller
                    name={'internshipStudyTypesId'}
                    control={control}
                    rules={{ required: t('required-field') }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <Select
                                atSelector={{
                                    container: 'internshipStudyTypesIdContainer',
                                    optionsContainer: 'internshipStudyTypesIdOptionsContainer',
                                    optionItem: 'internshipStudyTypesIdOptionsItem',
                                }}
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                onChoose={(chosen) => {
                                    onChange(chosen?.map((c) => c.value));
                                }}
                                items={
                                    studyTypes?.map((studyType) => ({ key: studyType.name, value: studyType.id })) || []
                                }
                                placeholder={t('internshipStudyTypesId')}
                                error={!!errors['internshipStudyTypesId']}
                            />
                            {errors?.internshipStudyTypesId && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.internshipStudyTypesId?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.educationIsRequired && (
                <>
                    <Controller
                        name={'university'}
                        control={control}
                        rules={{
                            required: t('required-field'),
                            maxLength: { value: 255, message: t('max-length', { value: 255 }) },
                        }}
                        render={({ field: { onChange, value } }) => (
                            <FieldRow>
                                <PlaceholderField data-at-selector={'internshipUniversityDescription'}>
                                    {t('university-placeholder')}
                                </PlaceholderField>
                                <TextField
                                    atSelector="universitySelector"
                                    fullWidth
                                    value={value}
                                    type={'text'}
                                    variant={'border'}
                                    transparent
                                    placeholder={t('university')}
                                    onChange={(value) => onChange(value)}
                                    error={!!errors['university']}
                                />
                                {errors?.university && (
                                    <Error>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    errors?.university?.message ??
                                                    DOMPurify.sanitize(t('validations:default')),
                                            }}
                                        />
                                    </Error>
                                )}
                            </FieldRow>
                        )}
                    />
                    <Controller
                        name={'faculty'}
                        control={control}
                        rules={{
                            required: t('required-field'),
                            maxLength: { value: 255, message: t('max-length', { value: 255 }) },
                        }}
                        render={({ field: { onChange, value } }) => (
                            <FieldRow>
                                <TextField
                                    atSelector="facultySelector"
                                    fullWidth
                                    value={value}
                                    type={'text'}
                                    variant={'border'}
                                    transparent
                                    placeholder={t('faculty')}
                                    onChange={(value) => onChange(value)}
                                    error={!!errors['faculty']}
                                />
                                {errors?.faculty && (
                                    <Error>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html:
                                                    errors?.faculty?.message ??
                                                    DOMPurify.sanitize(t('validations:default')),
                                            }}
                                        />
                                    </Error>
                                )}
                            </FieldRow>
                        )}
                    />
                    <FieldRowDouble>
                        <Controller
                            name={'startEducationStr'}
                            control={control}
                            rules={{ required: t('required-field') }}
                            render={({ field: { onChange, value } }) => (
                                <FieldRow>
                                    <Select
                                        atSelector={{
                                            container: 'startEducationStrContainer',
                                            optionsContainer: 'startEducationStrOptionsContainer',
                                            optionItem: 'startEducationStrOptionsItem',
                                        }}
                                        items={
                                            internshipSettings?.startEducationYears?.map((item) => ({
                                                key: item,
                                                value: item,
                                            })) || []
                                        }
                                        variant={'border'}
                                        fullWidth
                                        value={value}
                                        placeholder={t('startEducationStr')}
                                        onChoose={(chosen) => {
                                            if (chosen) {
                                                onChange(chosen[0].value);
                                            }
                                        }}
                                        error={!!errors['startEducationStr']}
                                    />
                                    {errors?.startEducationStr && (
                                        <Error>
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        errors?.startEducationStr?.message ??
                                                        DOMPurify.sanitize(t('validations:default')),
                                                }}
                                            />
                                        </Error>
                                    )}
                                </FieldRow>
                            )}
                        />
                        <Controller
                            name={'finishEducationStr'}
                            control={control}
                            rules={{ required: t('required-field') }}
                            render={({ field: { onChange, value } }) => (
                                <FieldRow>
                                    <Select
                                        atSelector={{
                                            container: 'finishEducationStrContainer',
                                            optionsContainer: 'finishEducationStrOptionsContainer',
                                            optionItem: 'finishEducationStrOptionsItem',
                                        }}
                                        items={
                                            internshipSettings?.finishEducationYears?.map((item) => ({
                                                key: item,
                                                value: item,
                                            })) || []
                                        }
                                        variant={'border'}
                                        fullWidth
                                        value={value}
                                        placeholder={t('finishEducationStr')}
                                        onChoose={(chosen) => {
                                            if (chosen) {
                                                onChange(chosen[0].value);
                                            }
                                        }}
                                        error={!!errors['finishEducationStr']}
                                    />
                                    {errors?.finishEducationStr && (
                                        <Error>
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        errors?.finishEducationStr?.message ??
                                                        DOMPurify.sanitize(t('validations:default')),
                                                }}
                                            />
                                        </Error>
                                    )}
                                </FieldRow>
                            )}
                        />
                    </FieldRowDouble>
                </>
            )}
            {internshipSettings?.hoursPerWeekIsRequired && (
                <Controller
                    name={'hoursPerWeek'}
                    control={control}
                    rules={{ required: t('required-field') }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <Select
                                atSelector={{
                                    container: 'hoursPerWeekContainer',
                                    optionsContainer: 'hoursPerWeekOptionsContainer',
                                    optionItem: 'hoursPerWeekOptionsItem',
                                }}
                                items={Object.keys(HoursPerWeek)
                                    .filter((key) => !isNaN(Number(HoursPerWeek[key as keyof typeof HoursPerWeek])))
                                    .map((hour) => ({
                                        key: t(hour),
                                        value: HoursPerWeek[hour as keyof typeof HoursPerWeek],
                                    }))}
                                variant={'border'}
                                fullWidth
                                value={value}
                                placeholder={t('hoursPerWeek')}
                                onChoose={(chosen) => {
                                    if (chosen) {
                                        onChange(chosen[0].value);
                                    }
                                }}
                                error={!!errors['hoursPerWeek']}
                            />
                            {errors?.hoursPerWeek && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.hoursPerWeek?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.englishLevelIsRequired && (
                <Controller
                    name={'englishLevel'}
                    control={control}
                    rules={{ required: t('required-field') }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <Select
                                atSelector={{
                                    container: 'englishLevelContainer',
                                    optionsContainer: 'englishLevelOptionsContainer',
                                    optionItem: 'englishLevelOptionsItem',
                                }}
                                items={Object.keys(EnglishLevel)
                                    .filter((key) => !isNaN(Number(EnglishLevel[key as keyof typeof EnglishLevel])))
                                    .map((level) => ({
                                        key: t(level),
                                        value: EnglishLevel[level as keyof typeof EnglishLevel],
                                    }))}
                                variant={'border'}
                                fullWidth
                                value={value}
                                placeholder={t('englishLevel')}
                                onChoose={(chosen) => {
                                    if (chosen) {
                                        onChange(chosen[0].value);
                                    }
                                }}
                                error={!!errors['englishLevel']}
                            />
                            {errors?.englishLevel && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.englishLevel?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.howDidYouKnowIsRequired && (
                <Controller
                    name={'howDidYouKnowTypesId'}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <Select
                                atSelector={{
                                    container: 'howDidYouKnowTypesIdContainer',
                                    optionsContainer: 'howDidYouKnowTypesIdOptionsContainer',
                                    optionItem: 'howDidYouKnowTypesIdOptionsItem',
                                }}
                                isMulti
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                onChoose={(chosen) => {
                                    onChange(chosen?.map((c) => c.value));
                                }}
                                items={
                                    howDidYouKnowTypes?.map((howDidYouKnow) => ({
                                        key: howDidYouKnow.name,
                                        value: howDidYouKnow.id,
                                    })) || []
                                }
                                placeholder={t('howDidYouKnowTypesId')}
                                error={!!errors['howDidYouKnowTypesId']}
                            />
                            {errors?.howDidYouKnowTypesId && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.howDidYouKnowTypesId?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.descriptionYourSelfIsRequired && (
                <Controller
                    name={'descriptionYourSelf'}
                    control={control}
                    rules={{
                        maxLength: { value: 990, message: t('max-length', { value: 990 }) },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="descriptionYourSelfSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('descriptionYourSelf')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['descriptionYourSelf']}
                                isTextArea
                            />
                            {errors?.descriptionYourSelf && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.descriptionYourSelf?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.whatYouWantToKnowAboutInternshipIsRequired && (
                <Controller
                    name={'whatYouWantToKnowAboutInternship'}
                    control={control}
                    rules={{
                        maxLength: { value: 990, message: t('max-length', { value: 990 }) },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="whatYouWantToKnowAboutInternshipSelector"
                                fullWidth
                                value={value}
                                type={'text'}
                                variant={'border'}
                                transparent
                                placeholder={t('whatYouWantToKnowAboutInternship')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['whatYouWantToKnowAboutInternship']}
                                isTextArea
                            />
                            {errors?.whatYouWantToKnowAboutInternship && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.whatYouWantToKnowAboutInternship?.message ??
                                                DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
            {internshipSettings?.refIdIsRequired && (
                <Controller
                    name={'refId'}
                    control={control}
                    rules={{
                        maxLength: { value: 10, message: t('max-length', { value: 10 }) },
                    }}
                    render={({ field: { onChange, value } }) => (
                        <FieldRow>
                            <TextField
                                atSelector="refIdSelector"
                                fullWidth
                                value={value}
                                type={'number'}
                                variant={'border'}
                                transparent
                                placeholder={t('refId')}
                                onChange={(value) => onChange(value)}
                                error={!!errors['refId']}
                            />
                            {errors?.refId && (
                                <Error>
                                    <span
                                        dangerouslySetInnerHTML={{
                                            __html:
                                                errors?.refId?.message ?? DOMPurify.sanitize(t('validations:default')),
                                        }}
                                    />
                                </Error>
                            )}
                        </FieldRow>
                    )}
                />
            )}
        </FormBase>
    );
};

export default RegistrationForm;
